import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { geolocationV2, masterManifest, sponsors } from "../../redux/actions";
import useAgeGateRegionKillswitch from "./useAgeGateRegionKillswitch";
import { resolveDemographicGates } from "../utils/gateResolver";

let lastFetchedPathname = null;

/**
 * Calls whereami v2 (Digitas) when the whereami killswitch is ON;
 * skips the call entirely when OFF. Fails open on error — no block,
 * no gates applied. On success, chains a master manifest fetch to
 * resolve compliance gates (AlcoholGate, BettingGate).
 */
const useWhereAmIV2 = ({ intervalMs } = {}) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const mixApiReady = useSelector((state) => !!state.mixapi.data);
  const geolocationV2State = useSelector((state) => state.geolocationV2);
  const masterManifestState = useSelector((state) => state.masterManifest);
  const sponsorsState = useSelector((state) => state.sponsors);
  // Full whereami v2 URL, sourced from web config (apps/web_config.json).
  const whereamiUrl = useSelector(
    (state) => state.commonConfig?.data?.config?.whereami
  );
  const { isAgeGatingFeatureEnabled, isWhereAmIEnabled } = useAgeGateRegionKillswitch();

  // Dispatch whereami v2 once per pathname; skip when whereAmIKillswitch disables it
  // or web config has no whereami URL.
  useEffect(() => {
    if (
      !mixApiReady ||
      !isWhereAmIEnabled ||
      !whereamiUrl ||
      pathname === lastFetchedPathname
    )
      return;
    lastFetchedPathname = pathname;
    dispatch(geolocationV2.getGeolocationV2(whereamiUrl));
  }, [pathname, mixApiReady, isWhereAmIEnabled, whereamiUrl, dispatch]);

  // Interval re-poll uses v2; only active when product confirms the cadence.
  useEffect(() => {
    if (!intervalMs || !mixApiReady || !whereamiUrl) return;
    const interval = setInterval(() => {
      dispatch(geolocationV2.getGeolocationV2(whereamiUrl));
    }, intervalMs);
    return () => clearInterval(interval);
  }, [intervalMs, mixApiReady, whereamiUrl, dispatch]);

  // Manifest + sponsors fetch, triggered unconditionally — independent of the
  // whereami/age-gating killswitch and of geo success/failure. Each is
  // dispatched at most once per session via its own loading/success/error guard.
  useEffect(() => {
    if (!mixApiReady) return;

    if (
      !masterManifestState.loading &&
      !masterManifestState.success &&
      !masterManifestState.error
    ) {
      dispatch(masterManifest.fetchMasterManifest());
    }

    if (
      !sponsorsState.loading &&
      !sponsorsState.success &&
      !sponsorsState.error
    ) {
      dispatch(sponsors.fetchSponsorsConfig());
    }
  }, [
    dispatch,
    mixApiReady,
    masterManifestState.loading,
    masterManifestState.success,
    masterManifestState.error,
    sponsorsState.loading,
    sponsorsState.success,
    sponsorsState.error,
  ]);

  const geolocationSettled = !!geolocationV2State.success || !!geolocationV2State.error;

  // Manifest is now dispatched unconditionally (see effect above), so it
  // always settles on its own — no geo-failure escape hatch needed here.
  const manifestSettled =
    !!masterManifestState.success || !!masterManifestState.error;

  // A missing whereami URL fails open, same as the whereAmIKillswitch being off.
  const canFetchLocationSpecificApis =
    mixApiReady &&
    (!isAgeGatingFeatureEnabled ||
      !isWhereAmIEnabled ||
      !whereamiUrl ||
      (geolocationSettled && manifestSettled));

  const demographicPayload = useMemo(() => {
    const activeGeoData = geolocationV2State.data;

    if (activeGeoData) {
      const resolvedGates = resolveDemographicGates({
        countryCodeAlpha3: activeGeoData.countryCodeAlpha3,
        region: activeGeoData.region,
        manifestData: masterManifestState.data,
      });
      return {
        CountryOfResidence: null,
        GeoLocation: activeGeoData.countryCodeAlpha3 || null,
        // GeoRegion only ever applies to USA — other countries' regions (if
        // any resolve from the geo provider) are not part of this contract.
        GeoRegion:
          activeGeoData.countryCodeAlpha3 === "USA"
            ? activeGeoData.region || null
            : null,
        AlcoholGate: resolvedGates.AlcoholGate,
        BettingGate: resolvedGates.BettingGate,
      };
    }

    // Fail-open: geo failed or is still loading / not yet started.
    return {
      CountryOfResidence: null,
      GeoLocation: null,
      GeoRegion: null,
      AlcoholGate: false,
      BettingGate: false,
    };
  }, [geolocationV2State.data, masterManifestState.data]);

  return {
    loading: geolocationV2State.loading,
    error: geolocationV2State.error,
    success: geolocationV2State.success,
    data: geolocationV2State.data,
    canFetchLocationSpecificApis,
    demographicPayload,
  };
};

export default useWhereAmIV2;
